import { useState } from "react";
import operatorApiService from "../../../../services/operator-api.service";
import "../../MasterModal.scss";

interface UserCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserCreateModal = ({
  isOpen,
  onClose,
  onSuccess,
}: UserCreateModalProps) => {
  const [roleCd, setRoleCd] = useState("");
  const [monitorConsoleId, setMonitorConsoleId] = useState<number | null>(null);
  const [userName, setUserName] = useState("");
  const [communicationCode, setCommunicationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[\W_]/.test(password);

    const typeCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;

    return minLength && typeCount >= 3;
  };

  const handleSubmit = async () => {
    if (
      !roleCd ||
      !monitorConsoleId ||
      !userName ||
      !communicationCode ||
      !password
    ) {
      setErrorMessage("必須項目が未入力です。");
      return;
    }

    if (
      roleCd.length > 2 ||
      userName.length > 16 ||
      communicationCode.length > 8 ||
      password.length > 255
    ) {
      setErrorMessage("文字数が制限を超えています。");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("パスワードが一致しません。");
      return;
    }

    if (!validatePassword(password)) {
      setErrorMessage(
        "パスワードは8文字以上で、英字・数字・記号の全てを含めてください。"
      );
      return;
    }

    try {
      await operatorApiService
        .createUser({
          role_cd: roleCd,
          monitor_console_id: monitorConsoleId,
          user_name: userName,
          communication_code: communicationCode,
          password: password,
        })
        .then((response) => {
          if (response.error) {
            setErrorMessage(response.error);
          }

          if (response.status === "success") {
            setErrorMessage(null);
            setSuccessMessage("登録に成功しました。");
            onSuccess?.();
            setTimeout(() => {
              setSuccessMessage(null);
              handleClose();
            }, 1000);
          }
        });
    } catch (error) {
      setErrorMessage("登録に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleClose = () => {
    setRoleCd("");
    setMonitorConsoleId(null);
    setUserName("");
    setCommunicationCode("");
    setPassword("");
    setConfirmPassword("");
    setSuccessMessage(null);
    setErrorMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="masterModal">
      <div className="modalOverlay">
        <div className="modalContent">
          <div className="modalHeader">
            <div className="modalTitle">ユーザ登録</div>
          </div>
          <div className="modalBody">
            <div className="inputGroup">
              <label>権限CD</label>
              <input
                value={roleCd}
                onChange={(e) => setRoleCd(e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>監視卓ID</label>
              <input
                type="number"
                value={monitorConsoleId ?? ""}
                onChange={(e) => setMonitorConsoleId(Number(e.target.value))}
              />
            </div>
            <div className="inputGroup">
              <label>ユーザ名</label>
              <input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>通信識別コード</label>
              <input
                value={communicationCode}
                onChange={(e) => setCommunicationCode(e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>パスワード</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>パスワード確認</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <div className="passwordNote">
              ※パスワードは、英字、数字、記号を含む8文字以上
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          </div>
          <div className="modalFooter">
            <button className="backButton" onClick={handleClose}>
              キャンセル
            </button>
            <button className="decisionButton" onClick={handleSubmit}>
              登録
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreateModal;
